import type { Class, ProjectKind } from '../data/types';
import { suggestForLabel } from './labelSuggestions';
import { templatesForKind, getTemplate } from './templates';
import { DEFAULT_WIDGET_ORDER, type PublishConfig, type TemplateId } from './types';

/**
 * Build a starter PublishConfig for a freshly-trained project, or
 * when the kid swaps templates inside the composer. Picks the first
 * template that fits the kind, seeds title/subtitle from the template
 * defaults, and gives every class a placeholder emoji so the preview
 * has something to render before the kid edits.
 */

const PLACEHOLDER_EMOJI = ['✨', '🎉', '🐶', '🐱', '🌟', '🎈', '🎯', '🍕', '🚀', '🌈'];

function emojiFor(index: number): string {
  return PLACEHOLDER_EMOJI[index % PLACEHOLDER_EMOJI.length]!;
}

export function defaultPublishConfigFor(
  kind: ProjectKind,
  classes: ReadonlyArray<Pick<Class, 'id'> & Partial<Pick<Class, 'name'>>>,
  projectName: string,
): PublishConfig {
  const template = templatesForKind(kind)[0]?.id ?? 'sticker-cam';
  return rebuildOnTemplateSwitch({
    template,
    classes,
    projectName,
    previous: undefined,
  });
}

/**
 * Compute a new PublishConfig when the kid swaps templates. We keep
 * the kid's text edits and per-class customisation when they apply
 * to the new template, and fall back to template defaults for
 * anything we can't carry over (e.g. layoutVariant indices don't
 * cross templates).
 */
export function rebuildOnTemplateSwitch(opts: {
  template: TemplateId;
  classes: ReadonlyArray<Pick<Class, 'id'> & Partial<Pick<Class, 'name'>>>;
  projectName: string;
  previous: PublishConfig | undefined;
}): PublishConfig {
  const { template, classes, projectName, previous } = opts;
  const def = getTemplate(template);
  const perClass: PublishConfig['perClass'] = {};
  classes.forEach((c, i) => {
    const carried = previous?.perClass[c.id];
    const suggested = c.name ? suggestForLabel(c.name, 1)[0] : undefined;
    perClass[c.id] = {
      emoji: carried?.emoji ?? suggested?.emoji ?? emojiFor(i),
      ...(carried?.label ? { label: carried.label } : {}),
      ...(carried?.soundId ? { soundId: carried.soundId } : {}),
    };
  });
  return {
    template,
    themeId: previous?.themeId ?? 'lab',
    layoutVariant: 0,
    text: {
      title: previous?.text.title ?? projectName ?? def.defaults.title,
      ...((previous?.text.subtitle ?? def.defaults.subtitle)
        ? { subtitle: previous?.text.subtitle ?? def.defaults.subtitle ?? '' }
        : {}),
      ...((previous?.text.actionLabel ?? def.defaults.actionLabel)
        ? {
            actionLabel: previous?.text.actionLabel ?? def.defaults.actionLabel ?? '',
          }
        : {}),
    },
    perClass,
    branding: { showFooter: previous?.branding.showFooter ?? true },
    widgets: previous?.widgets ?? { confidenceBars: false },
    widgetOrder: previous?.widgetOrder ?? DEFAULT_WIDGET_ORDER,
    version: (previous?.version ?? 0) + 1,
  };
}
